import {useMemo} from "react";
import moment from "moment";
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import {
	monthlyTypes,
	typeKey,
	typeColor,
	trainingTypePartial,
} from "@/utils/trainingTypes";
import TrainingExercisesRecap from "./TrainingExercisesRecap";
import "./training-types.scss";
const number = (value) => (value == null ? "—" : value.toLocaleString("it-IT"));
export default function TrainingTypes({
	analysis,
	typeColors,
	scope,
	setScope,
	metric,
	setMetric,
}) {
	const display = analysis;
	const monthly = useMemo(
		() => monthlyTypes(display, scope, metric),
		[display, scope, metric],
	);
	if (!analysis)
		return <p className="ram__note">Riepilogo tipologie non disponibile.</p>;
	const summary = display.period[scope];
	const months = monthly.rows;
	const incomplete =
		summary.sessionsWithoutExercises ||
		summary.missingDuration ||
		summary.withoutType ||
		summary.unknownAttendance;
	const slices = summary.types.filter((type) => type.minutes > 0);
	return (
		<section className="rtt" aria-label="Tipologie allenamento">
			<div className="rtt__header">
				<h3>Tipologie allenamento</h3>
				<div
					className="ram__kinds"
					role="group"
					aria-label="Stato allenamenti del riepilogo"
				>
					{[
						["completed", "Concluse"],
						["scheduled", "Programmate"],
					].map(([key, label]) => (
						<button
							type="button"
							key={key}
							aria-pressed={scope === key}
							onClick={() => setScope(key)}
						>
							{label} ({display.period[key].sessions})
						</button>
					))}
				</div>
			</div>
			<div className="rtt__visuals">
				<section
					className="rtt__period-chart"
					aria-label="Distribuzione tipologie del periodo"
				>
					<div className="rtt__chart-heading">
						<h4>Tipologie del periodo</h4>
						<small>Minuti registrati</small>
					</div>
					<div className="rtt__pie">
						{slices.length ? (
							<ResponsiveContainer width="100%" height={170}>
								<PieChart>
									<Pie
										data={slices}
										dataKey="minutes"
										nameKey="type"
										cx="50%"
										cy="50%"
										outerRadius={70}
										isAnimationActive
									>
										{slices.map((type) => (
											<Cell
												key={typeKey(type)}
												fill={typeColor(type, typeColors)}
											/>
										))}
									</Pie>
									<Tooltip
										formatter={(value, name) => [
											`${number(value)} min · ${Math.round((100 * value) / summary.minutes)}%`,
											name,
										]}
									/>
								</PieChart>
							</ResponsiveContainer>
						) : (
							<p className="ram__note">
								{summary.sessions
									? "Minuti degli esercizi non rilevati."
									: "Nessuna seduta nel periodo selezionato."}
							</p>
						)}
					</div>
					<p className="rtt__totals">
						<strong>{summary.sessions}</strong> sedute <span>·</span>{" "}
						<strong>{summary.exercises}</strong> esercizi <span>·</span>{" "}
						<strong>{number(summary.minutes)}′</strong> registrati negli
						esercizi
					</p>
					<details className="rtt__comparison rtt__period-details">
						<summary>
							Dettaglio del periodo{" "}
							{incomplete > 0 && (
								<small className="rtt__incomplete">Dati da completare</small>
							)}
						</summary>
						<div className="rtt__comparison-body">
							{" "}
							{!summary.sessions && !summary.unknownAttendance ? (
								<p className="ram__note">
									Nessuna seduta{" "}
									{scope === "completed" ? "conclusa" : "programmata"} nel
									periodo selezionato.
								</p>
							) : !summary.types.length ? (
								<p className="ram__note">
									Esercizi non ancora inseriti: tipologie e minuti non rilevati.
								</p>
							) : (
								<div className="rtt__table-wrap">
									<table className="rtt__table">
										<thead>
											<tr>
												<th>Tipologia</th>
												<th>Sedute</th>
												<th>Esercizi</th>
												<th>{"Minuti"}</th>
												<th>{"Distribuzione minuti"}</th>
											</tr>
										</thead>
										<tbody>
											{summary.types.map((type) => (
												<tr key={typeKey(type)}>
													<th scope="row">
														<i
															style={{
																background: typeColor(type, typeColors),
															}}
														/>
														{type.type}
													</th>
													<td>{type.sessions}</td>
													<td>{type.exercises}</td>
													<td>
														{number(type.minutes)}
														{(type.missingDuration > 0 ||
															type.unknownAttendance > 0) && (
															<small
																title={`${type.missingDuration} esercizi senza durata; ${type.unknownAttendance || 0} presenze da verificare`}
															>
																{" "}
																*
															</small>
														)}
													</td>
													<td>
														{type.minutes != null && summary.minutes > 0 ? (
															<div className="rtt__distribution">
																<span>
																	<i
																		style={{
																			width: `${(100 * type.minutes) / summary.minutes}%`,
																			background: typeColor(type, typeColors),
																		}}
																	/>
																</span>
																<small>
																	{Math.round(
																		(100 * type.minutes) / summary.minutes,
																	)}
																	%
																</small>
															</div>
														) : (
															"—"
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
							{(summary.sessionsWithoutExercises > 0 ||
								summary.missingDuration > 0 ||
								summary.withoutType > 0 ||
								summary.unknownAttendance > 0) && (
								<p className="rtt__warning">
									Da completare:{" "}
									{summary.unknownAttendance > 0 &&
										`${summary.unknownAttendance} presenze mancanti o da verificare · `}
									{summary.sessionsWithoutExercises} sedute senza esercizi ·{" "}
									{summary.missingDuration} esercizi senza durata ·{" "}
									{summary.withoutType} senza tipologia. * Minuti parziali o non
									rilevati.
								</p>
							)}
							<p className="ram__note">
								Una seduta può contenere più tipologie: i conteggi per tipologia
								non si sommano al totale sedute. Minuti degli esercizi inseriti,
								indipendenti dalla durata del calendario e dalla ricerca
								giocatore.
							</p>
						</div>
					</details>
				</section>
				<section
					className="rtt__monthly-chart"
					aria-label="Confronto mensile tipologie"
				>
					<div className="rtt__chart-heading">
						<h4>
							Confronto tra mesi <small>Intera stagione</small>
						</h4>{" "}
						<div
							className="ram__kinds"
							role="group"
							aria-label="Misura confronto mensile"
						>
							{[
								["minutes", "Minuti"],
								["sessions", "Sedute"],
							].map(([key, label]) => (
								<button
									type="button"
									key={key}
									aria-pressed={metric === key}
									onClick={() => setMetric(key)}
								>
									{label}
								</button>
							))}
						</div>
					</div>
					{monthly.types.length ? (
						<div className="rtt__chart">
							<ResponsiveContainer width="100%" height={170}>
								<BarChart
									data={months}
									margin={{
										top: 10,
										right: 8,
										left: -10,
										bottom: 0,
									}}
								>
									<CartesianGrid
										stroke="#e5edf6"
										strokeDasharray="3 3"
										vertical
									/>
									<XAxis
										dataKey="month"
										tickFormatter={(month) => moment(month).format("MMM YY")}
										tick={{
											fontSize: 11,
										}}
										interval="preserveStartEnd"
									/>
									<YAxis
										tick={{
											fontSize: 10,
										}}
										allowDecimals
									/>
									<Tooltip
										labelFormatter={(month) =>
											moment(month).format("MMMM YYYY")
										}
										formatter={(value, name) => [
											number(value) +
												(metric === "minutes" ? " min" : " sedute"),
											name,
										]}
									/>
									{monthly.types.map((type) => (
										<Bar
											key={typeKey(type)}
											name={type.type}
											dataKey={(row) => row[typeKey(type)]}
											fill={typeColor(type, typeColors)}
											stackId="types"
											maxBarSize={36}
										/>
									))}
								</BarChart>
							</ResponsiveContainer>
						</div>
					) : (
						<div className="rtt__empty">
							Nessuna tipologia registrata per lo stato selezionato.
						</div>
					)}
					<p className="ram__note">
						{scope === "completed" ? "Sedute concluse" : "Sedute programmate"} ·{" "}
						{metric === "minutes"
							? "Minuti registrati"
							: "Una seduta può contenere più tipologie"}
					</p>
					<details className="rtt__comparison rtt__monthly-details">
						<summary>Dettaglio del confronto mensile</summary>
						<div className="rtt__comparison-body">
							{" "}
							<div className="rtt__table-wrap">
								<table className="rtt__table rtt__months">
									<thead>
										<tr>
											<th>
												Tipologia / {metric === "minutes" ? "minuti" : "sedute"}
											</th>
											{months.map((month) => (
												<th key={month.month}>
													{moment(month.month).format("MMM YY")}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{monthly.types.map((type) => (
											<tr key={typeKey(type)}>
												<th scope="row">{type.type}</th>
												{months.map((month) => (
													<td key={month.month}>
														{number(month[typeKey(type)])}
														{trainingTypePartial(month.summary, type, metric)
															? " *"
															: ""}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<p className="ram__note">
								Solo dettagli registrati. I mesi senza sedute valgono zero; —
								indica dati non rilevati. Confronto mensile della stagione,
								indipendente dal periodo della matrice.{" "}
							</p>
							{months.some(
								(month) =>
									month.summary.sessionsWithoutExercises ||
									month.summary.missingDuration ||
									month.summary.withoutType ||
									month.summary.unknownAttendance,
							) && (
								<div className="rtt__coverage">
									{months
										.filter(
											(month) =>
												month.summary.sessions > 0 ||
												month.summary.unknownAttendance > 0,
										)
										.map((month) => (
											<span key={month.month}>
												<strong>{moment(month.month).format("MMM YY")}</strong>:{" "}
												{month.summary.sessions} sedute;{" "}
												{month.summary.unknownAttendance || 0} presenze da
												verificare; {month.summary.sessionsWithoutExercises}{" "}
												senza esercizi, {month.summary.missingDuration} durate
												mancanti, {month.summary.withoutType} tipi mancanti.
											</span>
										))}
								</div>
							)}
						</div>
					</details>
				</section>
				<TrainingExercisesRecap
					metric={metric}
					scope={scope}
					summary={analysis.period[scope]}
					exercises={
						scope === "scheduled"
							? analysis.scheduledExercises || []
							: analysis.exercises || []
					}
					typeColors={typeColors}
				/>
			</div>
			<div className="rtt__legend">
				{monthly.types.map((type) => (
					<span key={typeKey(type)}>
						<i
							style={{
								background: typeColor(type, typeColors),
							}}
						/>
						{type.type}
					</span>
				))}
			</div>
		</section>
	);
}
